import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const userData = await api.get('/api/auth/me');
      setUser(userData);
      setIsAuthenticated(true);
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const data = await api.post('/api/auth/login', { email, password });
    setUser(data.user);
    setIsAuthenticated(true);
    return data;
  };

  const register = async (userData) => {
    const data = await api.post('/api/auth/register', userData);
    // Registration now returns { message, email } — no session yet
    return data;
  };

  const verifyEmail = async (email, otp) => {
    const data = await api.post('/api/auth/verify-email', { email, otp });
    setUser(data.user);
    setIsAuthenticated(true);
    return data;
  };

  const resendOTP = async (email) => {
    return await api.post('/api/auth/resend-otp', { email });
  };

  const googleLogin = async (credential) => {
    const data = await api.post('/api/auth/google', { credential });
    setUser(data.user);
    setIsAuthenticated(true);
    return data;
  };

  const adminLogin = async (email, password) => {
    const data = await api.post('/api/admin/login', { email, password });
    setUser(data.user);
    setIsAuthenticated(true);
    return data;
  };

  const logout = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const updateProfile = async (updates) => {
    const updatedUser = await api.put('/api/users/me', updates);
    setUser(updatedUser);
    return updatedUser;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated,
        login,
        googleLogin,
        register,
        verifyEmail,
        resendOTP,
        adminLogin,
        logout,
        updateProfile,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
